import { Injectable } from '@angular/core';
import * as CryptoJS from 'crypto-js';
import { environment } from 'src/environments/environment';
import { safeJsonParse } from 'src/app/shared/utils/safe-json';

@Injectable({
  providedIn: 'root'
})
export class LocalStorageService {

  constructor() { }
    
    /**
    * store in in localstorage
    * encruption enabled
    * @param {JSON} data
    */
    public storeLoginData(data: any): void {
      if (!data) return;
      const loginData = environment.ENCRYPT_LOCAL_STORAGE ? CryptoJS.AES.encrypt(JSON.stringify(data), environment.LOCAL_STORAGE_SECRET).toString() : JSON.stringify(data);
      localStorage.setItem('login response', loginData);
    }
  
    /**
     * get stored used data from localstorage
     * @return {JSON} returns user data
     */
    public getLoginData(): any {
      const stored = localStorage.getItem('login response');
      if (stored === null) return null;
      return this.parseOrClear('login response', this.decrypt(stored));
    }
  
    /**
     * store any data
     *
     * @param {string} key
     * @param {any} value
     */
    public storeData(key: string, value: any): boolean {
      try {
        const data = environment.ENCRYPT_LOCAL_STORAGE ? CryptoJS.AES.encrypt(JSON.stringify(value), environment.LOCAL_STORAGE_SECRET).toString() : JSON.stringify(value);
        localStorage.setItem(key, data);
        return true;
      } catch (error) {
        console.error('Local storage error:', error);
        return false;
      }
    }
  
    /**
     * Get localstorage value by key
     *
     * @param {string} key
     * @return {any}
     */
    public getDataByKey(key: string): any {
      const stored = localStorage.getItem(key);
      if (stored === null) return null;
      return this.parseOrClear(key, this.decrypt(stored));
    }

    /**
     * Decrypts a raw localStorage value when encryption is enabled. Falls back to an empty
     * string on decryption failure (e.g. corrupted/garbage ciphertext) so callers can route
     * it through parseOrClear and self-heal instead of throwing.
     */
    private decrypt(raw: string): string {
      if (!environment.ENCRYPT_LOCAL_STORAGE) return raw;
      try {
        return CryptoJS.AES.decrypt(raw, environment.LOCAL_STORAGE_SECRET).toString(CryptoJS.enc.Utf8);
      } catch {
        return '';
      }
    }

    /**
     * Parses a raw localStorage value; if it isn't valid JSON, logs which key was corrupted
     * and removes it so the app self-heals instead of failing the same way on every load.
     */
    private parseOrClear(key: string, raw: string): any {
      return this.parseOrClearFrom(localStorage, key, raw);
    }

    /**
     * Same JSON (and optional AES) shape as {@link storeData}, written to sessionStorage.
     * A referral code has to survive the wallet-connect teardown, which clears localStorage,
     * and still die with the tab.
     */
    public storeSessionData(key: string, value: any): boolean {
      try {
        const data = environment.ENCRYPT_LOCAL_STORAGE ? CryptoJS.AES.encrypt(JSON.stringify(value), environment.LOCAL_STORAGE_SECRET).toString() : JSON.stringify(value);
        sessionStorage.setItem(key, data);
        return true;
      } catch (error) {
        console.error('Session storage error:', error);
        return false;
      }
    }

    /** Reads a sessionStorage value written by {@link storeSessionData}. */
    public getSessionData(key: string): any {
      const stored = sessionStorage.getItem(key);
      if (stored === null) return null;
      return this.parseOrClearFrom(sessionStorage, key, this.decrypt(stored));
    }

    public clearSessionData(key: string): void {
      sessionStorage.removeItem(key);
    }

    /**
     * Referral code captured from a `ref` query param.
     * Prefers sessionStorage. A value still sitting in localStorage (written before the
     * session move) is copied across once and removed, so signup and the Play Store link
     * keep seeing it after a wallet-connect teardown.
     */
    public getReferralValue(key: string): any {
      const fromSession = this.getSessionData(key);
      if (fromSession != null && fromSession !== '') return fromSession;
      const fromLocal = this.getDataByKey(key);
      if (fromLocal != null && fromLocal !== '') {
        this.storeSessionData(key, fromLocal);
        this.clearLocalStorage(key);
        return fromLocal;
      }
      return null;
    }

    /**
     * Drops `referralId` and `refId` from both storages.
     * Call this only when a wallet change or a logout sends the user to `/`.
     * The wallet-connect teardown must not call it — that page is mid referral flow.
     */
    public clearReferralIds(): void {
      this.clearSessionData('referralId');
      this.clearSessionData('refId');
      this.clearLocalStorage('referralId');
      this.clearLocalStorage('refId');
    }

    private parseOrClearFrom(storage: Storage, key: string, raw: string): any {
      const PARSE_FAILED = Symbol('parseFailed');
      const parsed = safeJsonParse<any>(raw, PARSE_FAILED as any);
      if (parsed === PARSE_FAILED) {
        console.warn(`LocalStorageService: corrupted value for key "${key}", clearing it`);
        storage.removeItem(key);
        return null;
      }
      return parsed;
    }
  
    /**
     * Params local storage service
     * @param {string}key
     */
    public clearLocalStorage(key: string): void {
      localStorage.removeItem(key);
    }

    /**
     * Clears all storage
     */
    public clearAllStorage() {
      localStorage.clear();
    }

      /**
   * Sets token
   * @param token 
   */

  setToken(token: string): void {
    localStorage.setItem('accessToken', token);
  }

  /**
   * Gets token
   * @returns token 
   */
  getToken(): string | null {
    return localStorage.getItem('accessToken');
  }

  /**
   * Removes token
   * @returns  
   */
  removeToken() {
    localStorage.removeItem('accessToken');
  } 

  defaultLanguage(): void {
   if(!localStorage.getItem('lang')) {
      this.storeData('lang', 'en');  
    }  }
}
